import { FC, RefObject } from "react";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { Marker, Popup } from "react-leaflet";
import { CloseIcon } from "../../../../../../assets/icons";
import "./style.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../../../common/routesUtils";
import { PageType } from "../../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../../context/BreadcrumbsProvider";
import {
	BaseNodeDTO,
	NetworkNodeRegistryDTO,
	NodeDetailsDTO,
	NodesMapItemDTO,
	NodesPageItemDTO,
} from "../../../../../../datasources/api-client/data-contracts";
import { markerIcon } from "../../../utils";
import { LabelText } from "./LabelText";

type MarkerNode = BaseNodeDTO | NetworkNodeRegistryDTO | NodesPageItemDTO | NodeDetailsDTO | NodesMapItemDTO;

interface Props {
	node: MarkerNode;
	markers: RefObject<Record<number, L.Marker>>;
	index: number;
	color: string;
	showDetailsOnClick?: boolean;
	onSelectNode?: (node: BaseNodeDTO) => void;
	onNavigate?: (node: MarkerNode) => void;
}

export const NodeMarker: FC<Props> = ({
	node,
	markers,
	index,
	color,
	showDetailsOnClick = true,
	onSelectNode,
	onNavigate,
}) => {
	const { t } = useTranslation("common");
	const navigateTo = useNavigate();
	const { visitPage } = useBreadcrumbs();

	const handleClosePopup = () => {
		markers.current[index]?.closePopup();
	};

	const handleSelectNode = () => {
		handleClosePopup();

		onSelectNode?.({
			typeId: node.typeId,
			typeDescription:
				"typeDescription" in node ? node.typeDescription : "typeName" in node ? node.typeName : undefined,
			code: node.code,
			city: node.city,
			address: node.address,
			zipCode: node.zipCode,
			province: "province" in node ? node.province : null,
			country: node.country,
			countryDescription: "countryDescription" in node ? node.countryDescription : null,
			lat: node.lat,
			lng: node.lng,
		});
	};

	const handleOpenDetails = () => {
		if (!("code" in node) || !node.code) return;

		if (onNavigate) {
			onNavigate(node);
			return;
		}

		const nodeCode = node.code;
		const nodeId = node.id;

		// Update breadcrumb stack with dynamic label and route id
		visitPage(PageType.ResultsOperationalUnitsGreenRouterDetail, false, nodeCode, nodeId);

		// Navigate to node detail
		navigateTo(AppRoutes.ResultOperationalUnitsGreerouterNode.replace(":id", nodeId));
	};

	return (
		<Marker
			icon={markerIcon(color, node.typeId)}
			position={[node.lat!, node.lng!]}
			interactive={showDetailsOnClick}
			ref={(ref) => {
				if (ref) {
					markers.current[index] = ref as unknown as L.Marker;
				}
			}}
		>
			{showDetailsOnClick && (
				<Popup autoPan={false} closeButton={false} className="custom-popup">
					<Stack spacing={0} minWidth={340} p={2}>
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							<Typography variant="h6" pr={1}>
								{"typeName" in node
									? node.typeName
									: "typeDescription" in node
										? node.typeDescription
										: ""}
							</Typography>

							<IconButton onClick={handleClosePopup}>
								<CloseIcon />
							</IconButton>
						</Stack>

						<Stack spacing={0.5} py={2}>
							<LabelText label={t("tables header/code")} value={node.code} textBold />

							<LabelText
								label={t("tables header/node type")}
								value={"typeDescription" in node ? node.typeDescription : ""}
							/>

							<LabelText label={t("tables header/address")} value={node.address} />

							<LabelText label={t("tables header/zip code")} value={node.zipCode} />

							<LabelText label={t("tables header/city")} value={node.city} />

							<LabelText
								label={t("tables header/country")}
								value={node.country}
								countryCode={node.country}
							/>
						</Stack>

						{onSelectNode ? (
							<Button
								fullWidth
								variant="contained"
								size="small"
								onClick={handleSelectNode}
								sx={{
									textTransform: "uppercase",
								}}
							>
								{t("select")}
							</Button>
						) : (
							<Button
								fullWidth
								variant="contained"
								size="small"
								onClick={handleOpenDetails}
								disabled={!("id" in node) || !node.id}
								sx={{
									textTransform: "uppercase",
								}}
							>
								{t("see details")}
							</Button>
						)}
					</Stack>
				</Popup>
			)}
		</Marker>
	);
};
